import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { useCart } from "../../lib/cart";
import { formatPrice, imgUrl } from "../../lib/format";
import { X, ShoppingBag, ArrowRight, ShieldCheck, PenNib, Plus, CreditCard } from "@phosphor-icons/react";
import { api } from "../../lib/api";
import { pickRelatedProducts, slugsInCart } from "../../lib/product-recommendations";
import {
  recommendForCart,
  CART_REC_HEADING,
  CART_REC_SUPPORT,
} from "../../lib/cart-recommendations";
import { isCompanionAccessory } from "../../lib/research-areas";
import { toast } from "sonner";

/**
 * Right-side cart drawer.
 *
 * 2026-08 redesign additions
 * --------------------------
 *   * Simplified: single strong "Continue to Secure Checkout" CTA
 *     (previous grid of View/Checkout collapsed into one primary action).
 *   * Trust footer: Stripe, server-validated pricing, signature at
 *     delivery — three factual bullets.
 *   * Optional "Frequently added together" strip: up to TWO catalog
 *     recommendations from the SAME chemistry category as the most
 *     recently added item.  Excludes items already in cart and the seed
 *     itself.  Each recommendation has an explicit "Add" button — nothing
 *     is ever auto-added.  Rendered only when a real, purchasable,
 *     in-stock recommendation exists.
 */
export const MiniCart = () => {
  const { drawerOpen, setDrawerOpen, items, subtotal, remove, update, add } = useCart();
  const nav = useNavigate();
  const [recs, setRecs] = useState([]);
  const seed = items.length > 0 ? items[items.length - 1] : null;

  // Lazy-load a recommendation pool the first time the drawer opens with
  // an item — same-category filter runs on the client so no new endpoint
  // is required.  Skips fetching when the pool is already loaded.
  useEffect(() => {
    if (!drawerOpen || !seed) { setRecs([]); return; }
    // Fetch a small pool (36 items) sorted by featured/price via the
    // existing `/products` endpoint; sufficient to find same-category
    // in-stock candidates for even the smallest category (typically 4-15
    // active SKUs).
    let cancelled = false;
    api.get("/products", { params: { limit: 200 } })
      .then((r) => {
        if (cancelled) return;
        const pool = r.data.items || [];
        // Deterministic, whole-cart aware engine — includes the
        // owner-mandated GLP → MOTS-C / AOD-9604 / GHK-Cu / Tesamorelin
        // priority rule.  Cap at 2 for the mini-cart.
        const engineRecs = recommendForCart(items, pool, 2);
        if (engineRecs.length > 0) {
          setRecs(engineRecs);
          return;
        }
        // Fallback path (only when the engine returns nothing) — surface
        // companion diluents first, then same-category legacy recommender.
        const excluded = slugsInCart(items);
        const purchasable = pool.filter(
          (p) => p && p.active && p.ecommerce_enabled &&
                 p.checkout_status === "Add to Cart" &&
                 p.card_payment_allowed && p.public_price_visible &&
                 (p.retail_price_cents || 0) > 0 &&
                 !excluded.includes(p.slug),
        );
        const companions = purchasable.filter(isCompanionAccessory).slice(0, 1);
        const sameCat = pickRelatedProducts(
          seed, pool,
          [...excluded, ...companions.map((c) => c.slug)],
          Math.max(0, 2 - companions.length),
        );
        setRecs([...companions, ...sameCat].slice(0, 2));
      })
      .catch(() => setRecs([]));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, seed?.slug, items.length]);

  const handleAddRec = async (product) => {
    try {
      await add(product, 1, { openDrawer: false });
      toast.success(`${product.product_name} added to cart`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add to cart");
    }
  };

  return (
    <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
      <SheetContent
        side="right"
        data-testid="mini-cart-drawer"
        className="w-full sm:max-w-md bg-white border-l border-brand-border text-brand-navy p-0 flex flex-col"
      >
        <SheetHeader className="p-5 border-b border-brand-border flex-row items-center justify-between space-y-0">
          <SheetTitle className="text-brand-navy font-display flex items-center gap-2 text-lg">
            <ShoppingBag size={18} weight="duotone" className="text-brand-accent" /> Cart
            {items.length > 0 && (
              <span className="ml-1 text-xs font-mono text-brand-muted" data-testid="mini-cart-count">
                · {items.reduce((s, i) => s + i.quantity, 0)}
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-10 text-center" data-testid="mini-cart-empty">
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-brand-surface border border-brand-border mb-4">
                <ShoppingBag size={22} weight="regular" className="text-brand-navy" />
              </div>
              <div className="font-display text-lg font-semibold text-brand-navy">Your cart is empty.</div>
              <p className="mt-2 text-brand-muted text-sm">
                Add a reference standard from the catalog to continue.
              </p>
              <Link
                to="/shop"
                onClick={() => setDrawerOpen(false)}
                data-testid="mini-cart-empty-cta"
                className="mt-6 inline-flex items-center gap-2 h-11 px-5 rounded-[12px] bg-brand-navy hover:bg-brand-navy2 text-white font-semibold text-sm transition-colors"
              >
                Browse the catalog <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          ) : (
            <>
              <div className="divide-y divide-brand-border">
                {items.map((it) => (
                  <div key={it.product_id} className="p-4 flex gap-3" data-testid={`mini-cart-item-${it.slug}`}>
                    {it.thumbnail_image_url && (
                      <img
                        src={imgUrl(it.thumbnail_image_url)}
                        alt={it.image_alt_text || it.public_display_name}
                        className="w-16 h-16 object-contain bg-brand-surface border border-brand-border rounded-[10px] p-1.5 flex-shrink-0"
                        loading="lazy"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-display font-semibold text-brand-navy truncate">
                        {it.public_display_name}
                      </div>
                      <div className="text-[11px] text-brand-muted font-mono">
                        {it.strength} · {it.public_sku}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="flex items-center border border-brand-border rounded-[8px] overflow-hidden bg-white">
                          <button
                            onClick={() => update(it.product_id, Math.max(1, it.quantity - 1))}
                            className="px-2.5 py-1 text-brand-text text-xs hover:bg-brand-surface transition-colors"
                            data-testid={`mini-cart-dec-${it.slug}`}
                            aria-label="Decrease quantity"
                          >−</button>
                          <span className="px-2.5 text-xs font-mono font-semibold text-brand-navy min-w-[1.5rem] text-center">
                            {it.quantity}
                          </span>
                          <button
                            onClick={() => update(it.product_id, it.quantity + 1)}
                            className="px-2.5 py-1 text-brand-text text-xs hover:bg-brand-surface transition-colors"
                            data-testid={`mini-cart-inc-${it.slug}`}
                            aria-label="Increase quantity"
                          >+</button>
                        </div>
                        <div className="ml-auto font-mono text-sm font-semibold text-brand-navy">
                          {formatPrice(it.unit_amount_cents * it.quantity)}
                        </div>
                        <button
                          onClick={() => remove(it.product_id)}
                          className="text-brand-muted hover:text-red-500 p-1 transition-colors"
                          aria-label="Remove"
                          data-testid={`mini-cart-remove-${it.slug}`}
                        >
                          <X size={14} weight="bold" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Optional recs — max TWO, category-aware, explicit Add per item */}
              {recs.length > 0 && (
                <div
                  data-testid="mini-cart-recommendations"
                  className="border-t border-brand-border bg-brand-surface"
                >
                  <div className="px-4 pt-4 pb-1">
                    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-brand-accent">
                      Suggested pairings
                    </div>
                    <div
                      className="mt-1 text-sm font-display font-semibold text-brand-navy leading-snug"
                      data-testid="mini-cart-rec-heading"
                    >
                      {CART_REC_HEADING}
                    </div>
                    <p
                      className="mt-1 text-[11px] text-brand-muted leading-relaxed"
                      data-testid="mini-cart-rec-support"
                    >
                      {CART_REC_SUPPORT}
                    </p>
                  </div>
                  <div className="divide-y divide-brand-border">
                    {recs.map((p) => (
                      <div
                        key={p.slug}
                        data-testid={`mini-cart-rec-${p.slug}`}
                        className="p-3 flex gap-3 items-center"
                      >
                        {p.thumbnail_image_url && (
                          <img
                            src={imgUrl(p.thumbnail_image_url)}
                            alt={p.image_alt_text || p.public_display_name}
                            className="w-12 h-12 object-contain bg-white border border-brand-border rounded-[8px] p-1 flex-shrink-0"
                            loading="lazy"
                          />
                        )}
                        <Link
                          to={`/product/${p.slug}`}
                          onClick={() => setDrawerOpen(false)}
                          className="flex-1 min-w-0"
                        >
                          <div className="text-sm font-display font-semibold text-brand-navy truncate hover:text-brand-accent transition-colors">
                            {p.public_display_name || p.product_name}
                          </div>
                          <div className="text-[11px] text-brand-muted font-mono truncate">
                            {p.strength}{p.public_sku ? ` · ${p.public_sku}` : ""}
                          </div>
                        </Link>
                        <div className="text-right shrink-0">
                          <div className="font-mono text-xs font-semibold text-brand-navy mb-1">
                            {formatPrice(p.retail_price_cents)}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleAddRec(p)}
                            data-testid={`mini-cart-rec-add-${p.slug}`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold bg-white text-brand-navy border border-brand-navy hover:bg-brand-navy hover:text-white transition-colors"
                          >
                            <Plus size={10} weight="bold" /> Add
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-brand-border p-5 space-y-4 bg-white">
            <div className="flex items-center justify-between text-sm">
              <span className="text-brand-muted font-medium">Subtotal</span>
              <span
                className="font-display font-bold text-lg text-brand-navy"
                data-testid="mini-cart-subtotal"
              >
                {formatPrice(subtotal)}
              </span>
            </div>
            <div className="text-[11px] text-brand-muted">
              Shipping and tax are calculated at secure checkout.
            </div>

            <button
              onClick={() => { setDrawerOpen(false); nav("/cart"); }}
              data-testid="mini-cart-checkout-btn"
              className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-[12px] bg-brand-accent hover:bg-[#1E6BD8] text-white font-semibold text-sm transition-[background-color,transform,box-shadow] duration-200 hover:-translate-y-[1px] hover:shadow-soft"
            >
              Continue to Secure Checkout <ArrowRight size={14} weight="bold" />
            </button>

            <button
              onClick={() => setDrawerOpen(false)}
              data-testid="mini-cart-continue-btn"
              className="w-full text-[12px] font-medium text-brand-muted hover:text-brand-navy py-1 transition-colors"
            >
              ← Continue shopping
            </button>

            {/* Trust footer — three factual bullets, no fake urgency */}
            <div
              className="border-t border-brand-border pt-3 grid grid-cols-1 gap-2 text-[11px] text-brand-muted"
              data-testid="mini-cart-trust"
            >
              <div className="flex items-center gap-2">
                <CreditCard size={13} weight="regular" className="text-brand-accent shrink-0" aria-hidden />
                Secure Stripe checkout
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck size={13} weight="regular" className="text-brand-accent shrink-0" aria-hidden />
                Prices are checked securely before payment
              </div>
              <div className="flex items-center gap-2">
                <PenNib size={13} weight="regular" className="text-brand-accent shrink-0" aria-hidden />
                Signature required at delivery
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default MiniCart;
